import Calendar16 from '@carbon/icons-react/es/calendar/16';
import Error16 from '@carbon/icons-react/es/error/16';
import Flag16 from '@carbon/icons-react/es/flag/16';
import ListChecked16 from '@carbon/icons-react/es/list--checked/16';
import Search16 from '@carbon/icons-react/es/search/16';
import UserAvatar16 from '@carbon/icons-react/es/user--avatar/16';
import UserFollow16 from '@carbon/icons-react/es/user--follow/16';
import WarningAlt16 from '@carbon/icons-react/es/warning--alt/16';
import React from 'react';
import { StatusFieldOption, StatusGroup, TaskType } from 'api/tasks';
import { View, ViewType } from 'api/views/types';
import { defaultFilter } from 'components/molecules/filters/utils';
import { Views } from 'components/molecules/views';
import { load } from 'components/molecules/views/core';
import { ColumnOrder } from 'components/organisms/grid-custom-column';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import {
  AssigneeField,
  BooleanField,
  DateField,
  FilterableField,
  SingleSelectField,
  SingleUserField,
  TextField,
  UserId,
} from 'components/organisms/tasks/fields';
import { BooleanFilterOperationType } from 'components/organisms/tasks/filters/boolean';
import { DateFieldFilterOperationType } from 'components/organisms/tasks/filters/date';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { TextFieldFilterOperationType } from 'components/organisms/tasks/filters/text';
import { Translate } from 'hooks/use-translation';
import { FieldHelper } from 'utils/field';
import {
  defaultKanbanBoardColumnGroup,
  defaultKanbanBoardRowGroup,
} from '../constants';
import { TasksViewDefinition } from './types';

interface Props {
  lookup: Record<string, FilterableField>;
  userId: number;
  isFieldsLoading: boolean;
  translate: Translate;
  defaultColumnOrder?: ColumnOrder[];
}
export const computeDefaultTaskViews = ({
  userId,
  lookup,
  isFieldsLoading,
  translate,
  defaultColumnOrder = [],
}: Props): [Views<TasksViewDefinition>, number] => {
  const defaultViewId = -1;
  const groupBy = {
    rowGroup: defaultKanbanBoardRowGroup,
    columnGroup: defaultKanbanBoardColumnGroup,
  };

  const assignee = lookup.assignee as AssigneeField;
  const created_by = lookup.createdBy as SingleUserField;
  const status = lookup.status as SingleSelectField;
  const atRisk = lookup.atRisk as BooleanField;
  const dueDate = lookup.dueDate as DateField;
  const type = lookup.type as TextField;

  const commonViewData = {
    createdBy: -1,
    self: false,
    viewType: ViewType.AllTasks,
  };

  const allTaskView = {
    viewId: -1,
    viewName: translate('all_tasks.filters.all_tasks'),
    viewIcon: <ListChecked16 />,
    viewDefinition: {
      filter: defaultFilter,
      groupBy,
    },
    createdBy: -1,
    self: false,
    viewType: ViewType.AllTasks,
  };

  if (isFieldsLoading) {
    return [load([allTaskView]), defaultViewId];
  }
  const completed = status
    ? FieldHelper(status)
        .options.activeOptions.filter(
          (x) => (x as StatusFieldOption).group === StatusGroup.Completed,
        )
        .map(({ value }) => value)
    : undefined;
  const blocked = status
    ? FieldHelper(status)
        .options.activeOptions.filter(
          (x) => (x as StatusFieldOption).group === StatusGroup.Blocked,
        )
        .map(({ value }) => value)
    : undefined;

  const viewsList: View<TasksViewDefinition>[] = [
    allTaskView,
    {
      viewId: -2,
      viewName: translate('all_tasks.filters.my_tasks'),
      viewIcon: <UserAvatar16 />,
      viewDefinition: {
        filter: {
          criteria: [
            {
              field: assignee,
              type: SelectFilterOperationType.IsOneOf,
              value: [UserId(userId)],
            },
          ],
          type: QueryCombinerType.All,
        },
        groupBy,
      },
    },
    {
      viewId: -3,
      viewName: translate('all_tasks.filters.created_by_me'),
      viewIcon: <Search16 />,
      viewDefinition: {
        filter: {
          criteria: [
            {
              field: created_by,
              type: SelectFilterOperationType.Is,
              value: userId,
            },
          ],
          type: QueryCombinerType.All,
        },
        groupBy,
      },
    },
    {
      viewId: -4,
      viewName: translate('all_tasks.filters.overdue'),
      viewIcon: <Calendar16 />,
      viewDefinition: {
        filter: {
          criteria: [
            {
              field: dueDate,
              type: DateFieldFilterOperationType.IsBeforeToday,
            },
            {
              field: status,
              type: SelectFilterOperationType.IsNoneOf,
              value: completed,
            },
          ],
          type: QueryCombinerType.All,
        },
        groupBy,
      },
    },
    {
      viewId: -5,
      viewName: translate('all_tasks.filters.unassigned'),
      viewIcon: <UserFollow16 />,
      viewDefinition: {
        filter: {
          criteria: [
            {
              field: assignee,
              type: SelectFilterOperationType.IsEmpty,
            },
          ],
          type: QueryCombinerType.All,
        },
        groupBy,
      },
    },
    {
      viewId: -6,
      viewName: translate('all_tasks.filters.no_due_date'),
      viewIcon: <Calendar16 />,
      viewDefinition: {
        filter: {
          criteria: [
            {
              field: dueDate,
              type: DateFieldFilterOperationType.IsEmpty,
            },
          ],
          type: QueryCombinerType.All,
        },
        groupBy,
      },
    },
    {
      viewId: -7,
      viewName: translate('all_tasks.filters.at_risk'),
      viewIcon: <WarningAlt16 />,
      viewDefinition: {
        filter: {
          criteria: [
            {
              field: atRisk,
              type: BooleanFilterOperationType.IsTrue,
            },
          ],
          type: QueryCombinerType.All,
        },
        groupBy,
      },
    },
    {
      viewId: -8,
      viewName: translate('all_tasks.filters.blocked'),
      viewIcon: <Error16 />,
      viewDefinition: {
        filter: {
          criteria: [
            {
              field: status,
              type: SelectFilterOperationType.IsOneOf,
              value: blocked,
            },
          ],
          type: QueryCombinerType.All,
        },
        groupBy,
      },
    },
    {
      viewId: -9,
      viewName: translate('all_tasks.filters.milestones'),
      viewIcon: <Flag16 />,
      viewDefinition: {
        filter: {
          criteria: [
            {
              field: type,
              type: TextFieldFilterOperationType.Contains,
              value: TaskType.Milestone,
            },
          ],
          type: QueryCombinerType.All,
        },
        groupBy,
      },
    },
  ].map(
    (view) =>
      ({
        ...commonViewData,
        ...view,
        viewDefinition: {
          ...view.viewDefinition,
          columnOrder: defaultColumnOrder,
        },
      } as View<TasksViewDefinition>),
  );

  return [load(viewsList), defaultViewId];
};
